class TanteMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.reset = this.reset.bind(this);
    this.tileSliderCleanups = [];
    this.preventTileClick = false;
  }

  connectedCallback() {
    if (this.dataset.drawerInitialized === 'true') return;

    this.dataset.drawerInitialized = 'true';
    this.addEventListener('click', this.onClick);
    document.addEventListener('menudrawer:close', this.reset);
    this.initTileCarousels();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('menudrawer:close', this.reset);
    this.tileSliderCleanups.forEach((cleanup) => cleanup());
    this.tileSliderCleanups = [];
  }

  onClick(event) {
    if (this.preventTileClick && event.target.closest('.tante-menu-drawer__tiles')) {
      event.preventDefault();
      this.preventTileClick = false;
      return;
    }

    let button = event.target.closest('[data-drawer-toggle]');

    if (!button || !this.contains(button)) {
      const row = event.target.closest('.tante-menu-drawer__row, .tante-menu-drawer__subrow');
      if (!row || !this.contains(row)) return;

      button = row.querySelector('[data-drawer-toggle]');
      if (!button) return;
    }

    const panelId = button.getAttribute('aria-controls');
    const panel = panelId && document.getElementById(panelId);
    if (!panel || !this.contains(panel)) return;

    event.preventDefault();

    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(willOpen));
    button.closest('.tante-menu-drawer__accordion')?.classList.toggle('is-open', willOpen);
    this.animatePanel(panel, willOpen);
  }

  animatePanel(panel, willOpen) {
    panel.drawerAnimation?.cancel();

    if (typeof panel.animate !== 'function') {
      panel.hidden = !willOpen;
      return;
    }

    const startHeight = willOpen ? 0 : panel.offsetHeight;
    if (willOpen) panel.hidden = false;
    const endHeight = willOpen ? panel.scrollHeight : 0;

    panel.style.height = `${startHeight}px`;
    panel.style.overflow = 'hidden';

    const animation = panel.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      {
        duration: willOpen ? 400 : 250,
        easing: willOpen ? 'ease-out' : 'ease',
      }
    );
    panel.drawerAnimation = animation;

    animation.onfinish = () => {
      if (panel.drawerAnimation !== animation) return;
      panel.drawerAnimation = null;
      panel.hidden = !willOpen;
      panel.style.height = '';
      panel.style.overflow = '';
    };

    animation.oncancel = () => {
      if (panel.drawerAnimation === animation) panel.drawerAnimation = null;
    };
  }

  initTileCarousels() {
    this.querySelectorAll('.tante-menu-drawer__tiles').forEach((slider) => {
      const state = {
        active: false,
        dragging: false,
        pointerId: null,
        startX: 0,
        startScrollLeft: 0,
      };

      const onPointerDown = (event) => {
        if (event.button !== 0 || event.pointerType === 'touch') return;

        state.active = true;
        state.dragging = false;
        state.pointerId = event.pointerId;
        state.startX = event.clientX;
        state.startScrollLeft = slider.scrollLeft;
        this.preventTileClick = false;
      };

      const onPointerMove = (event) => {
        if (!state.active) return;

        const distance = event.clientX - state.startX;
        if (!state.dragging && Math.abs(distance) > 4) {
          state.dragging = true;
          this.preventTileClick = true;
          slider.classList.add('is-dragging');
          if (state.pointerId != null && slider.setPointerCapture) {
            slider.setPointerCapture(state.pointerId);
          }
        }
        if (state.dragging) event.preventDefault();

        slider.scrollLeft = state.startScrollLeft - distance;
      };

      const endPointerDrag = () => {
        if (!state.active) return;

        state.active = false;
        if (state.dragging && state.pointerId != null && slider.releasePointerCapture) {
          slider.releasePointerCapture(state.pointerId);
        }
        state.dragging = false;
        state.pointerId = null;
        slider.classList.remove('is-dragging');
      };

      const preventNativeDrag = (event) => event.preventDefault();

      slider.addEventListener('pointerdown', onPointerDown);
      slider.addEventListener('pointermove', onPointerMove);
      slider.addEventListener('pointerup', endPointerDrag);
      slider.addEventListener('pointercancel', endPointerDrag);
      slider.addEventListener('dragstart', preventNativeDrag);

      this.tileSliderCleanups.push(() => {
        slider.removeEventListener('pointerdown', onPointerDown);
        slider.removeEventListener('pointermove', onPointerMove);
        slider.removeEventListener('pointerup', endPointerDrag);
        slider.removeEventListener('pointercancel', endPointerDrag);
        slider.removeEventListener('dragstart', preventNativeDrag);
      });
    });
  }

  reset() {
    this.querySelectorAll('[data-drawer-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.closest('.tante-menu-drawer__accordion')?.classList.remove('is-open');

      const panelId = button.getAttribute('aria-controls');
      const panel = panelId && document.getElementById(panelId);
      if (panel && this.contains(panel)) {
        panel.drawerAnimation?.cancel();
        panel.drawerAnimation = null;
        panel.hidden = true;
        panel.style.height = '';
        panel.style.overflow = '';
      }
    });
  }
}

if (!customElements.get('tante-menu-drawer')) {
  customElements.define('tante-menu-drawer', TanteMenuDrawer);
}
